import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";

function ExpenseForm() {
    const [expense, setExpense] = useState({
        name: "",
        amount: "",
        incurred_on: "",
        description: "",
        is_route_specific: false,
        route_id: "",
    });
    const [collectionRoutes, setCollectionRoutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const token = localStorage.getItem("access_token");

    useEffect(() => {
        const fetchCollectionRoutes = async () => {
            try {
                const response = await fetch(`${API_URL}/collection-routes`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    setCollectionRoutes(data);
                } else if (response.status === 401) {
                    navigate("/logout");
                } else {
                    throw new Error("Failed to fetch routes");
                }
            } catch (error) {
                setNotification({ type: "danger", message: "Failed to fetch collection routes." });
            }
        };

        const fetchExpense = async () => {
            if (id) {
                try {
                    const response = await fetch(`${API_URL}/expenses/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (response.ok) {
                        const data = await response.json();
                        // Format the date for the input field
                        const formattedData = {
                            ...data,
                            incurred_on: data.incurred_on ? data.incurred_on.split('T')[0] : "",
                            is_route_specific: data.is_route_specific || false,
                        };
                        setExpense(formattedData);
                        setIsEditing(true);
                    } else if (response.status === 401) {
                        navigate("/logout");
                    } else if (response.status === 404) {
                        setNotification({ type: "danger", message: "Expense not found." });
                        navigate("/dashboard/expenses");
                    } else {
                        throw new Error("Failed to fetch expense details");
                    }
                } catch (error) {
                    setNotification({ type: "danger", message: "An error occurred while fetching the expense." });
                }
            }
        };

        const fetchData = async () => {
            await fetchCollectionRoutes();
            await fetchExpense();
            setLoading(false);
        };

        fetchData();
    }, [id, token, navigate]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setExpense((prevExpense) => ({
            ...prevExpense,
            [name]: type === "checkbox" ? checked : value || "",
        }));
        
        // Clear validation errors for the field being changed
        if (errors[name]) {
            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrors({});
        setLoading(true);

        // Validate route_id when is_route_specific is true
        if (expense.is_route_specific && !expense.route_id) {
            setErrors(prevErrors => ({
                ...prevErrors,
                route_id: ["Route is required when expense is route specific."]
            }));
            setLoading(false);
            return;
        }

        const method = isEditing ? "PUT" : "POST";
        const url = isEditing 
            ? `${API_URL}/expenses/${id}` 
            : `${API_URL}/expenses`;

        // Prepare data - convert boolean to proper format
        const expenseData = {
            ...expense,
            is_route_specific: expense.is_route_specific ? 1 : 0,
            route_id: expense.is_route_specific ? expense.route_id : null,
        };

        // Remove empty values
        Object.keys(expenseData).forEach(key => {
            if (expenseData[key] === "" || expenseData[key] === null) {
                delete expenseData[key];
            }
        });

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(expenseData),
            });

            if (response.ok) {
                const data = await response.json();
                setNotification({ 
                    type: "success", 
                    message: isEditing ? "Expense updated successfully!" : "Expense created successfully!" 
                });
                // Navigate after a short delay to show success message
                setTimeout(() => {
                    navigate("/dashboard/expenses");
                }, 1000);
            } else {
                const errorData = await response.json();
                if (errorData.errors) {
                    setErrors(errorData.errors);
                } else {
                    setErrors({ general: [errorData.message || "An error occurred."] });
                }
                setNotification({ 
                    message: errorData.message || "An error occurred while saving the expense.", 
                    type: "danger" 
                });
            }
        } catch (error) {
            setNotification({ 
                message: "An error occurred while saving the expense.", 
                type: "danger" 
            });
        } finally {
            setLoading(false);
        }
    };

    // Get today's date for the max attribute on date input
    const today = new Date().toISOString().split('T')[0];

    if (loading && id) {
        return (
            <div className="has-text-centered" style={{ padding: "3rem" }}>
                <i className="fa fa-spinner fa-spin fa-2x"></i>
                <p>Loading expense details...</p>
            </div>
        );
    }

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">{isEditing ? "Update Expense" : "Add Expense"}</p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/expenses" className="button primary-bg">
                            <i className="fa fa-arrow-left"></i> &nbsp; Back
                        </Link>
                    </p>
                </div>
            </nav>

            {notification.message && (
                <Notification 
                    type={notification.type} 
                    message={notification.message} 
                    onClose={() => setNotification({})} 
                />
            )}

            {errors.general && errors.general.map((error, index) => (
                <div key={index} className="notification is-danger">
                    {error}
                </div>
            ))}

            <form onSubmit={handleSubmit}>
                <div className="columns">
                    <div className="column is-half">
                        {/* Expense Name */}
                        <div className="field">
                            <label className="label">Expense Name <span className="has-text-danger">*</span></label>
                            <div className="control">
                                <input 
                                    className={`input ${errors.name ? "is-danger" : ""}`} 
                                    type="text" 
                                    name="name" 
                                    value={expense.name} 
                                    onChange={handleChange} 
                                    placeholder="e.g., Fuel, Maintenance, etc."
                                    required 
                                    disabled={loading}
                                />
                            </div>
                            {errors.name && errors.name.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        {/* Amount */}
                        <div className="field">
                            <label className="label">Amount <span className="has-text-danger">*</span></label>
                            <div className="control">
                                <input 
                                    className={`input ${errors.amount ? "is-danger" : ""}`} 
                                    type="number" 
                                    name="amount" 
                                    value={expense.amount} 
                                    onChange={handleChange} 
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0.01"
                                    required 
                                    disabled={loading}
                                />
                            </div>
                            {errors.amount && errors.amount.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        {/* Incurred On Date */}
                        <div className="field">
                            <label className="label">Incurred On <span className="has-text-danger">*</span></label>
                            <div className="control">
                                <input 
                                    className={`input ${errors.incurred_on ? "is-danger" : ""}`} 
                                    type="date" 
                                    name="incurred_on" 
                                    value={expense.incurred_on} 
                                    onChange={handleChange} 
                                    max={today}
                                    required 
                                    disabled={loading}
                                />
                            </div>
                            {errors.incurred_on && errors.incurred_on.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        {/* Description */}
                        <div className="field">
                            <label className="label">Description</label>
                            <div className="control">
                                <textarea 
                                    className={`textarea ${errors.description ? "is-danger" : ""}`} 
                                    name="description" 
                                    value={expense.description || ""} 
                                    onChange={handleChange} 
                                    placeholder="Enter a description for this expense (optional)"
                                    rows="3"
                                    disabled={loading}
                                />
                            </div>
                            {errors.description && errors.description.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        {/* Is Route Specific - Checkbox */}
                        <div className="field">
                            <div className="control">
                                <label className="checkbox">
                                    <input 
                                        type="checkbox" 
                                        name="is_route_specific" 
                                        checked={expense.is_route_specific} 
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                    &nbsp; This expense is specific to a collection route
                                </label>
                            </div>
                            {errors.is_route_specific && errors.is_route_specific.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        {/* Collection Route - Conditional */}
                        {expense.is_route_specific && (
                            <div className="field">
                                <label className="label">Collection Route <span className="has-text-danger">*</span></label>
                                <div className={`select is-fullwidth ${errors.route_id ? "is-danger" : ""}`}>
                                    <select 
                                        name="route_id" 
                                        value={expense.route_id} 
                                        onChange={handleChange}
                                        disabled={loading}
                                        required={expense.is_route_specific}
                                    >
                                        <option value="">Select Route</option>
                                        {collectionRoutes?.map((route) => (
                                            <option key={route.id} value={route.id}>
                                                {route.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {errors.route_id && errors.route_id.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>
                        )}

                        {/* Show route info when not route specific */}
                        {!expense.is_route_specific && (
                            <div className="field">
                                <div className="notification is-light">
                                    <i className="fa fa-info-circle"></i>&nbsp;
                                    This expense will be applied to all routes as a general expense.
                                </div>
                            </div>
                        )}

                        {/* Submit Button */}
                        <div className="field">
                            <div className="control">
                                <button 
                                    type="submit" 
                                    className={`button is-primary ${loading ? "is-loading" : ""}`}
                                    disabled={loading}
                                >
                                    {isEditing ? "Update Expense" : "Save Expense"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </main>
    );
}

export default ExpenseForm;